import { useState, type FormEvent } from 'react';
import { SERVICE_CLASS_LABELS, SERVICE_CLASSES, FUEL_TYPE_LABELS, FUEL_TYPES, TAX_RATES, VEHICLE_KIND_LABELS, VEHICLE_KINDS, VEHICLE_STATUS_LABELS, VEHICLE_STATUSES, assetValue, berlinDate, formatCents, type Vehicle, type VehicleKind, type VehicleStatus, type FuelType } from '@kontor/shared';
import { centsText, parseCents } from '../../money';
import { useSession } from '../../session';
import { useEmployees } from './shared';

export interface VehicleDraft {
  plate: string;
  name: string;
  kind: VehicleKind;
  manufacturer: string;
  model: string;
  color: string;
  serviceClass: string;
  passengerCapacity: string;
  vin: string;
  firstRegistration: string;
  fuel: FuelType;
  odometerKm: string;
  employeeId: string;
  status: VehicleStatus;
  huDueOn: string;
  acquiredOn: string;
  cost: string;
  taxRate: string;
  years: string;
  note: string;
}

export const draftOf = (v?: Vehicle): VehicleDraft => ({
  plate: v?.plate ?? '',
  name: v?.name ?? '',
  kind: v?.kind ?? 'car',
  manufacturer: v?.manufacturer ?? '',
  model: v?.model ?? '',
  color: v?.color ?? '',
  serviceClass: v?.serviceClass ?? '',
  passengerCapacity: v?.passengerCapacity != null ? String(v.passengerCapacity) : '',
  vin: v?.vin ?? '',
  firstRegistration: v?.firstRegistration ?? '',
  fuel: v?.fuel ?? 'petrol',
  odometerKm: v ? String(v.currentKm) : '',
  employeeId: v?.employeeId ?? '',
  status: v?.status ?? 'active',
  huDueOn: v?.huDueOn ?? '',
  acquiredOn: v?.acquiredOn ?? '',
  cost: v?.acquisitionGrossCents != null ? centsText(v.acquisitionGrossCents) : '',
  taxRate: String(v?.taxRate ?? 19),
  years: v?.depreciationYears != null ? String(v.depreciationYears) : '',
  note: v?.note ?? '',
});

/** Daten für die API. Beim Anlegen gehört der Kilometerstand dazu, beim Ändern nicht (ihn schreiben Fahrten, Wartung und Kosten fort). */
export function vehicleBody(d: VehicleDraft, opts: { create: boolean; withCost: boolean; originalKm?: number }): Record<string, unknown> {
  const cost = d.cost.trim() ? parseCents(d.cost) : null;
  if (opts.withCost && d.cost.trim() && cost === null) throw new Error('Bitte geben Sie die Anschaffungskosten als Betrag ein, zum Beispiel 35.700,00.');
  const km = d.odometerKm.trim() ? Number(d.odometerKm.replace(/\./g, '')) : 0;
  if (!Number.isInteger(km) || km < 0) throw new Error('Bitte geben Sie den Kilometerstand als ganze Zahl an.');
  if (d.passengerCapacity.trim() && (!Number.isInteger(Number(d.passengerCapacity)) || Number(d.passengerCapacity) < 1)) throw new Error('Bitte geben Sie die Passagierkapazität als ganze Zahl an, zum Beispiel 6.');
  return {
    plate: d.plate.trim(),
    name: d.name.trim() || null,
    kind: d.kind,
    manufacturer: d.manufacturer.trim() || null,
    model: d.model.trim() || null,
    color: d.color.trim() || null,
    serviceClass: d.serviceClass || null,
    passengerCapacity: d.passengerCapacity.trim() ? Number(d.passengerCapacity) : null,
    vin: d.vin.trim() || null,
    firstRegistration: d.firstRegistration || null,
    fuel: d.fuel,
    // Beim Ändern nur, wenn der Stand korrigiert wurde
    ...(opts.create || (opts.originalKm !== undefined && km !== opts.originalKm) ? { odometerKm: km } : {}),
    employeeId: d.employeeId || null,
    status: d.status,
    huDueOn: d.huDueOn || null,
    acquiredOn: d.acquiredOn || null,
    ...(opts.withCost ? { acquisitionGrossCents: cost, taxRate: cost === null ? null : Number(d.taxRate), depreciationYears: d.years.trim() ? Number(d.years) : null } : {}),
    note: d.note.trim() || null,
  };
}

function ValuePreview({ draft }: { draft: VehicleDraft }) {
  const gross = draft.cost.trim() ? parseCents(draft.cost) : null;
  const years = draft.years.trim() ? Number(draft.years) : null;
  const v = assetValue({ grossCents: gross, taxRate: Number(draft.taxRate), acquiredOn: draft.acquiredOn || null, years: years && years >= 1 ? years : null, asOf: berlinDate() });
  if (v.netCents === null) return null;
  return (
    <p className="muted small field--wide" aria-live="polite">
      Netto {formatCents(v.netCents)} + Steuer {formatCents(v.taxCents ?? 0)} = brutto {formatCents(gross ?? 0)}.
      {v.bookValueCents !== null ? ` Restwert heute nach linearer Abschreibung: ${formatCents(v.bookValueCents)}.` : ' Für den Restwert fehlen Anschaffungsdatum und Nutzungsdauer (bei Pkw meist 6 Jahre).'}
    </p>
  );
}

/** Formular für Fahrzeuge, zum Anlegen und Ändern */
export function VehicleForm({ vehicle, onSubmit, onCancel, pending, error, submitLabel }: { vehicle?: Vehicle; onSubmit: (d: VehicleDraft) => void; onCancel: () => void; pending: boolean; error: string | null; submitLabel: string }) {
  const { can, session } = useSession();
  const employees = useEmployees();
  const [d, setD] = useState<VehicleDraft>(() => draftOf(vehicle));
  const set = (patch: Partial<VehicleDraft>) => setD({ ...d, ...patch });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit(d);
  };
  return (
    <form className="card fleet-form" onSubmit={submit} aria-label="Fahrzeug">
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <h2>{vehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</h2>
      <div className="form-grid">
        <label className="field"><span>Kennzeichen</span><input className="input" value={d.plate} onChange={(e) => set({ plate: e.target.value })} maxLength={15} required autoFocus placeholder="M AB 123" /></label>
        <label className="field"><span>Bezeichnung (optional)</span><input className="input" value={d.name} onChange={(e) => set({ name: e.target.value })} maxLength={100} placeholder="Limousine 1" /></label>
        <label className="field">
          <span>Art</span>
          <select className="input" value={d.kind} onChange={(e) => set({ kind: e.target.value as VehicleKind })}>{VEHICLE_KINDS.map((k) => <option key={k} value={k}>{VEHICLE_KIND_LABELS[k]}</option>)}</select>
        </label>
        <label className="field"><span>Hersteller</span><input className="input" value={d.manufacturer} onChange={(e) => set({ manufacturer: e.target.value })} maxLength={100} /></label>
        <label className="field"><span>Modell</span><input className="input" value={d.model} onChange={(e) => set({ model: e.target.value })} maxLength={100} /></label>
        <label className="field"><span>Farbe</span><input className="input" value={d.color} onChange={(e) => set({ color: e.target.value })} maxLength={40} placeholder="zum Beispiel Schwarz" /></label>
        {session?.tenant.industry === 'chauffeur' && (
          <label className="field">
            <span>Serviceklasse</span>
            <select className="input" value={d.serviceClass} onChange={(e) => set({ serviceClass: e.target.value })}>
              <option value="">Keine Klasse</option>
              {SERVICE_CLASSES.map((k) => <option key={k} value={k}>{SERVICE_CLASS_LABELS[k]}</option>)}
            </select>
            <small className="muted">Ein Auftrag mit verlangter Klasse bekommt nur Fahrzeuge dieser Klasse. Ohne Klasse passt das Fahrzeug zu keinem Auftrag mit Anforderung.</small>
          </label>
        )}
        <label className="field"><span>Fahrgestellnummer (FIN)</span><input className="input" value={d.vin} onChange={(e) => set({ vin: e.target.value })} maxLength={20} placeholder="17 Zeichen" /></label>
        <label className="field"><span>Erstzulassung</span><input className="input" type="date" value={d.firstRegistration} onChange={(e) => set({ firstRegistration: e.target.value })} /></label>
        <label className="field">
          <span>Kraftstoff</span>
          <select className="input" value={d.fuel} onChange={(e) => set({ fuel: e.target.value as FuelType })}>{FUEL_TYPES.map((f) => <option key={f} value={f}>{FUEL_TYPE_LABELS[f]}</option>)}</select>
        </label>
        <label className="field">
          <span>Kilometerstand</span>
          <input className="input" inputMode="numeric" value={d.odometerKm} onChange={(e) => set({ odometerKm: e.target.value })} placeholder="0" />
          {vehicle && <small className="muted">Korrektur des aktuellen Stands. Er darf nicht unter dem höchsten Stand aus Fahrtenbuch, Wartung und Kosten liegen.</small>}
        </label>
        <label className="field">
          <span>Passagierkapazität (Plätze)</span>
          <input className="input" inputMode="numeric" value={d.passengerCapacity} onChange={(e) => set({ passengerCapacity: e.target.value.replace(/\D/g, '') })} maxLength={2} placeholder="zum Beispiel 6" />
          <small className="muted">Sitzplätze für Fahrgäste ohne Fahrer. Hat eine Fahrt mehr Personen, warnt die Disposition.</small>
        </label>
        <label className="field">
          <span>Fahrer (Standard)</span>
          <select className="input" value={d.employeeId} onChange={(e) => set({ employeeId: e.target.value })}>
            <option value="">Niemand</option>
            {vehicle?.employeeId && !employees.data?.some((e) => e.id === vehicle.employeeId) && <option value={vehicle.employeeId}>{vehicle.employeeName ?? 'Mitarbeiter'}</option>}
            {employees.data?.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select className="input" value={d.status} onChange={(e) => set({ status: e.target.value as VehicleStatus })}>
            {VEHICLE_STATUSES.filter((s) => s !== 'sold' || vehicle).map((s) => <option key={s} value={s}>{VEHICLE_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="field"><span>Nächste HU/AU (TÜV)</span><input className="input" type="date" value={d.huDueOn} onChange={(e) => set({ huDueOn: e.target.value })} /></label>
        <label className="field"><span>Angeschafft am</span><input className="input" type="date" value={d.acquiredOn} onChange={(e) => set({ acquiredOn: e.target.value })} /></label>
        {can('fleet.values') && (
          <>
            <label className="field"><span>Anschaffungskosten brutto (€)</span><input className="input" inputMode="decimal" value={d.cost} onChange={(e) => set({ cost: e.target.value })} placeholder="59.500,00" /></label>
            <label className="field">
              <span>Steuersatz</span>
              <select className="input" value={d.taxRate} onChange={(e) => set({ taxRate: e.target.value })}>{TAX_RATES.map((r) => <option key={r} value={r}>{r} %</option>)}</select>
            </label>
            <label className="field"><span>Nutzungsdauer (Jahre)</span><input className="input" type="number" min={1} max={50} step={1} value={d.years} onChange={(e) => set({ years: e.target.value })} placeholder="6" /></label>
            <ValuePreview draft={d} />
          </>
        )}
        <label className="field field--wide"><span>Notiz</span><textarea className="input textarea textarea--short" value={d.note} onChange={(e) => set({ note: e.target.value })} maxLength={2000} /></label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={pending}>{pending ? 'Wird gespeichert …' : submitLabel}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
